import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalPayments } from "../../features/portal/portal-money-slice";
import { selectPortalMandate } from "../../features/portal/portal-slice";
import { fetchPortalPayments } from "../../features/portal/slice-extensions/portal-money-slice-extension";
import { portalPaymentAction } from "../../features/portal/utils/portalPaymentAction";
import PortalPaymentRow from "./PortalPaymentRow";

/** The id the "Set up direct debit first" action scrolls to. */
export const PORTAL_MANDATE_ANCHOR = "portal-mandate";

/**
 * Every Payment of the shown member, newest first, a page at a time. Each row offers one action
 * (Pay now, the way to the mandate card, or the debit in flight) and the issued document.
 */
const PortalPaymentsList = ({ memberId }: { memberId: string }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const list = useAppSelector(selectPortalPayments(memberId));
  const mandate = useAppSelector(selectPortalMandate(memberId));

  const reload = useCallback(() => {
    dispatch(fetchPortalPayments({ memberId }));
  }, [dispatch, memberId]);

  useEffect(reload, [reload]);

  const scrollToMandate = () =>
    document.getElementById(PORTAL_MANDATE_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "center" });

  if (!list || (list.status === "loading" && list.items.length === 0)) {
    return <p className="mt-6 text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>;
  }

  return (
    <div className="mt-6">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-grey-40 dark:text-grey-80">
        {t("portal.money.payments")}
      </p>
      {list.status === "failed" && list.items.length === 0 && (
        <p className="text-sm font-medium text-red-700 dark:text-red-400">{t("portal.money.loadFailed")}</p>
      )}
      {list.status !== "failed" && list.items.length === 0 && (
        <p className="text-sm text-grey-40 dark:text-grey-80">{t("portal.money.empty")}</p>
      )}
      <ul className="flex flex-col gap-2">
        {list.items.map((payment) => (
          <PortalPaymentRow
            key={payment.id}
            memberId={memberId}
            payment={payment}
            action={portalPaymentAction(payment, mandate)}
            onSetUpDebit={scrollToMandate}
            onStale={reload}
          />
        ))}
      </ul>
      {list.page < list.totalPages && (
        <button
          type="button"
          className="btn btn-text mt-3"
          disabled={list.status === "loading"}
          onClick={() => dispatch(fetchPortalPayments({ memberId, page: list.page + 1 }))}
        >
          {t("portal.money.showMore")}
        </button>
      )}
    </div>
  );
};

export default PortalPaymentsList;
